import {
  Fragment,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Popover, Transition } from '@headlessui/react';
import { MenuIcon, XIcon } from '@heroicons/react/outline';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { Link, RichText } from 'prismic-reactjs';
import SliceZone from 'next-slicezone';
import { nanoid } from 'nanoid';
import { Document } from '@prismicio/client/types/documents';
import Image from 'next/image';
import { useLinkResolver } from '../hooks/useLinkResolver';
import { getVerticalByLink, prismicLinkResolver } from '../utils/common';
import LinkWithQuery from './LinkWithQuery';
import { getColor } from '../utils/colors';
import HeaderBU from './HeaderBU';
import HeaderProductsMenu from './HeaderProductsMenu';
import CurrencyMenu from './CurrencyMenu/CurrencyMenu';

enum MenuContext {
  products = 'products',
  content = 'content',
  bu = 'bu',
}

interface Props {
  data: Document['data'];
  resolver
}

const Header: FunctionComponent<Props> = ({ data, resolver }) => {
  let timeout: NodeJS.Timeout;
  const timeoutDuration = 0;

  const slices = data.slices || data.body;

  const buttonRef = useRef<HTMLButtonElement>(null);
  const buttonContentRef = useRef<HTMLButtonElement>(null);
  const buttonBusinessUnitRef = useRef<HTMLButtonElement>(null);
  const buttonClustersRef = useRef<HTMLButtonElement>(null);
  const [openState, setOpenState] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileTouch, setMobileTouch] = useState(false);
  const homePageLink = useLinkResolver('/');

  const toggleMenu = (context?: string) => {
    setOpenState((openStatePrev) => !openStatePrev);
    let contextRef;
    if (context) {
      switch (context) {
        case MenuContext.products:
          contextRef = buttonRef;
          break;
        case MenuContext.content:
          contextRef = buttonContentRef;
          break;
        case MenuContext.bu:
          contextRef = buttonBusinessUnitRef;
          break;

        default:
          contextRef = null;
          break;
      }

      contextRef?.current?.click();
      contextRef?.current?.blur();
    }
  };

  const onHover = (open: boolean, context: string, action: string) => {
    if (mobileTouch) return;

    if (
      (!open && !openState && action === 'onMouseEnter')
      || (open && openState && action === 'onMouseLeave')
    ) {
      clearTimeout(timeout);
      timeout = setTimeout(() => toggleMenu(context), timeoutDuration);
    }
  };

  const onMobileTouch = (action: string) => {
    if (action === 'onTouchStart') {
      setMobileTouch(true);
    } else if (action === 'onMouseUp') {
      setMobileTouch(false);
    }
  };

  const handleClick = (open: boolean) => {
    setOpenState(!open);
    clearTimeout(timeout);
  };

  const handleMobileClick = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleClickOutside = (event) => {
    const isProductsButton = buttonRef.current?.contains(event.target);
    const isContentButton = buttonContentRef.current?.contains(event.target);
    const isBusinessUnitsButton = buttonBusinessUnitRef.current?.contains(event.target);
    const isClustersButton = buttonClustersRef.current?.contains(event.target);

    if (!isProductsButton && !isContentButton && !isBusinessUnitsButton && !isClustersButton) {
      event.stopPropagation();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  return (
    <Popover className="border-b border-b-gray-2 bg-black-1 z-[60] -mb-px sticky top-0">
      <div className={classNames(
        'dh-content py-4 lg:py-6 flex justify-between items-center',
        'h-10 lg:h-15 lg:ml-0 lg:pl-[3.875rem] lg:justify-start lg:max-w-full',
        'w-full md:justify-start md:space-x-12 3xl:max-w-8x5l 3xl:m-auto',
      )}
      >
        <div>
          <a className="flex" href={homePageLink}>
            <span className="sr-only">Digital House</span>
            <div className="max-h-4 w-42">
              {
                Boolean(data.logo?.url)
                && (
                <Image
                  src={data.logo.url}
                  alt={data.logo.alt}
                  width={data.logo.dimensions.width}
                  height={data.logo.dimensions.height}
                  layout="responsive"
                  objectFit="contain"
                  priority
                />
                )
              }
            </div>
          </a>
        </div>
        <div className="-mr-2 -my-2 md:hidden">
          <Popover.Button className={classNames(
            'bg-white dark:bg-black rounded-md p-2 inline-flex items-center justify-center',
            'text-gray-400 dark:text-white hover:text-gray-500 hover:bg-gray-100 focus:outline-none',
            'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
          )}
          >
            <span className="sr-only">
              <FormattedMessage
                defaultMessage="Abrir menú"
                description="navbar button"
              />
            </span>
            <MenuIcon className="h-6 w-6" aria-hidden="true" onClick={handleMobileClick} />
          </Popover.Button>
        </div>
        <div className="hidden md:flex-1 md:flex md:items-center md:justify-between">
          <Popover.Group as="nav" className="flex space-x-12 w-full">
            <div className="grow flex space-x-12">
              {data.products?.length > 0 && data.productsLabel && (
                <Popover className="relative">
                  {({ open }) => (
                  // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                    <div
                      className="pb-3 -mb-3"
                      onMouseEnter={() => onHover(open, MenuContext.products, 'onMouseEnter')}
                      onMouseLeave={() => onHover(open, MenuContext.products, 'onMouseLeave')}
                      onTouchStart={() => onMobileTouch('onTouchStart')}
                      onMouseUp={() => onMobileTouch('onMouseUp')}
                    >
                      <Popover.Button ref={buttonRef}>
                        {/* eslint-disable-next-line max-len */}
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                        <div
                          className={classNames(
                            open
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-500 dark:text-gray-300',
                            'group lg:bg-white lg:dark:bg-black rounded-md inline-flex items-center text-base',
                            'font-medium hover:text-gray-900 dark:hover:text-white focus:outline-none',
                            'focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:ring-primary-500',
                            'cursor-default',
                          )}
                          onClick={() => handleClick(open)}
                        >
                          {data.productsLabel}
                          <ChevronDownIcon
                            className={classNames(
                              open
                                ? 'text-gray-600 dark:text-white'
                                : 'text-gray-400',
                              'ml-2 h-5 w-5 group-hover:text-gray-500 dark:group-hover:text-white dark:text-gray-300',
                            )}
                            aria-hidden="true"
                          />
                        </div>
                      </Popover.Button>

                      <Transition
                        show
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel
                          className="absolute z-50 -ml-4 mt-3 transform w-screen max-w-md lg:max-w-3xl"
                          static
                        >
                          <div className="rounded-lg shadow-lg ring-1 ring-black/5 overflow-hidden">
                            <div className={classNames(
                              'relative grid gap-6 bg-white dark:bg-gray-900 px-5 py-6 sm:gap-8 sm:p-8 md:grid-cols-2',
                            )}
                            >
                              {data.products.map((item) => {
                                const vertical = getVerticalByLink(item.link, data.verticals, data.clusters);
                                const verticalColor = getColor(vertical.color);

                                return (
                                  <LinkWithQuery
                                    key={nanoid()}
                                    href={Link.url(item.link, prismicLinkResolver)}
                                    target={item.link?.target ?? '_self'}
                                    className={classNames(
                                      '-m-3 p-3 flex items-stretch rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800',
                                    )}
                                  >
                                    <div
                                      className={classNames('rounded-sm', verticalColor.bg)}
                                      style={{
                                        minWidth: '4px',
                                        backgroundColor: item.color,
                                      }}
                                    />
                                    <div className="ml-4">
                                      <p className="text-base font-medium text-gray-900 dark:text-gray-300">
                                        {item.title}
                                      </p>
                                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-300 md:hidden lg:block">
                                        {RichText.asText(item.description)}
                                      </p>
                                    </div>
                                  </LinkWithQuery>
                                );
                              })}
                            </div>
                            {data.seeMoreProductsLink
                            && data.seeMoreProductsLabel && (
                              <div className="px-5 py-5 bg-gray-50 dark:bg-gray-900 sm:px-8 sm:py-8">
                                <LinkWithQuery
                                  href={Link.url(
                                    data.seeMoreProductsLink,
                                    prismicLinkResolver,
                                  )}
                                  target={data.seeMoreProductsLink?.target ?? '_self'}
                                  className={classNames(
                                    'font-medium text-primary hover:text-primary-500 dark:text-white',
                                    'dark:hover:text-primary-400 transition ease-in-out duration-150',
                                  )}
                                >
                                  {data.seeMoreProductsLabel}
                                  {' '}
                                  <span aria-hidden="true">&rarr;</span>
                                </LinkWithQuery>
                              </div>
                            )}
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </div>
                  )}
                </Popover>
              )}

              {
              /** @deprecated use HeaderProductsMenu */
              !!data.businessUnit?.length && data.labelProductsByBu && (
              <Popover className="isolate z-20">
                {({ open }) => (
                  <div
                    onMouseEnter={() => onHover(open, MenuContext.bu, 'onMouseEnter')}
                    onMouseLeave={() => onHover(open, MenuContext.bu, 'onMouseLeave')}
                    onTouchStart={() => onMobileTouch('onTouchStart')}
                    onMouseUp={() => onMobileTouch('onMouseUp')}
                  >
                    <Popover.Button
                      ref={buttonBusinessUnitRef}
                      className="inline-flex items-center gap-x-1 font-medium leading-6 text-white-1"
                    >
                      {data.labelProductsByBu}
                      <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                    </Popover.Button>

                    <Transition
                      show={open}
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 -translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 -translate-y-1"
                    >
                      <Popover.Panel static className="absolute inset-x-0 -z-10 bg-black-1 text-white-3 top-15">
                        <HeaderBU data={data} />
                      </Popover.Panel>
                    </Transition>
                  </div>
                )}
              </Popover>
              )
            }

              {!!data.productsMenu?.length && !!data.labelProductsMenu && (
                <Popover className="isolate z-20">
                  {({ open }) => (
                    <div
                      onMouseEnter={() => onHover(open, MenuContext.bu, 'onMouseEnter')}
                      onMouseLeave={() => onHover(open, MenuContext.bu, 'onMouseLeave')}
                      onTouchStart={() => onMobileTouch('onTouchStart')}
                      onMouseUp={() => onMobileTouch('onMouseUp')}
                    >
                      <Popover.Button
                        ref={buttonBusinessUnitRef}
                        className="inline-flex items-center gap-x-1 font-medium leading-6 text-white-1"
                      >
                        {data.labelProductsMenu}
                        <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                      </Popover.Button>

                      <Transition
                        show={open}
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 -translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 -translate-y-1"
                      >
                        <Popover.Panel static className="absolute inset-x-0 -z-10 bg-black-1 text-white-3 top-15">
                          <HeaderProductsMenu data={data.productsMenu} clusters={data.clusters} />
                        </Popover.Panel>
                      </Transition>
                    </div>
                  )}
                </Popover>
              )}

              {data.links?.map((link) => (
                <LinkWithQuery
                  key={nanoid()}
                  href={Link.url(link.link, prismicLinkResolver)}
                  target={link.link?.target ?? '_self'}
                  className={classNames(
                    'text-base font-medium text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white',
                  )}
                >
                  {link.label}
                </LinkWithQuery>
              ))}
              {data.content?.length > 0 && (
                <Popover className="relative">
                  {({ open }) => (
                    <div
                      className="pb-3 -mb-3"
                      onMouseEnter={() => onHover(open, MenuContext.content, 'onMouseEnter')}
                      onMouseLeave={() => onHover(open, MenuContext.content, 'onMouseLeave')}
                      onTouchStart={() => onMobileTouch('onTouchStart')}
                      onMouseUp={() => onMobileTouch('onMouseUp')}
                    >
                      <Popover.Button ref={buttonContentRef}>
                        <div
                          className={classNames(
                            open
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-500 dark:text-gray-300',
                            'group lg:bg-white lg:dark:bg-black rounded-md inline-flex items-center text-base',
                            'font-medium hover:text-gray-900 dark:hover:text-white focus:outline-none',
                            'focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:ring-primary-500',
                            'cursor-default',
                          )}
                          onClick={() => handleClick(open)}
                        >
                          {data.contentLabel}
                          <ChevronDownIcon
                            className={classNames(
                              open
                                ? 'text-gray-600 dark:text-white'
                                : 'text-gray-400',
                              'ml-2 h-5 w-5 group-hover:text-gray-500 dark:group-hover:text-white dark:text-gray-300',
                            )}
                            aria-hidden="true"
                          />
                        </div>
                      </Popover.Button>

                      <Transition
                        show={open}
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel
                          className="absolute z-50 -ml-4 mt-3 transform"
                          static
                        >
                          <div className="rounded-lg shadow-lg ring-1 ring-black/5 overflow-hidden">
                            <div className={classNames(
                              'relative grid gap-6 bg-white dark:bg-gray-900 p-6',
                            )}
                            >
                              {data.content.map((item) => (
                                <LinkWithQuery
                                  key={nanoid()}
                                  href={Link.url(item.link, prismicLinkResolver)}
                                  target={item.link?.target ?? '_self'}
                                  className={classNames(
                                    'text-base font-medium text-gray-500 hover:text-gray-900',
                                    'dark:text-gray-300 dark:hover:text-white w-max',
                                  )}
                                >
                                  {item.label}
                                </LinkWithQuery>
                              ))}
                            </div>
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </div>
                  )}
                </Popover>
              )}
            </div>
            <div className="pb-3 -mb-3">
              <CurrencyMenu />
            </div>
          </Popover.Group>
          {data.loginButton && (
            <div className="flex items-center md:ml-12">
              <button
                type="button"
                className={classNames(
                  'ml-8 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md',
                  'shadow-sm text-base font-medium text-white dark:text-black bg-primary-600 hover:bg-primary-700',
                )}
              >
                <FormattedMessage
                  defaultMessage="Ingresar"
                  description="login button"
                />
              </button>
            </div>
          )}
        </div>
      </div>

      <Transition
        show={mobileOpen}
        as={Fragment}
        enter="duration-200 ease-out"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="duration-100 ease-in"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Popover.Panel
          focus
          className="absolute top-0 inset-x-0 p-2 transition transform origin-top-right md:hidden z-50"
        >
          <div className={classNames(
            'rounded-lg shadow-lg ring-1 ring-black/5 bg-white',
            'dark:bg-black divide-y-2 divide-gray-50 dark:divide-black dark:text-white-3',
          )}
          >
            <div className="pt-5 pb-6 px-5">
              <div className="flex items-center justify-between">
                <a href={homePageLink}>
                  <span className="sr-only">Digital House</span>
                  <div className="max-h-10 w-52">
                    {
                      Boolean(data.logo?.url)
                      && (
                        <Image
                          src={data.logo.url}
                          alt={data.logo.alt}
                          width={data.logo.dimensions.width}
                          height={data.logo.dimensions.height}
                          layout="responsive"
                          objectFit="contain"
                        />
                      )
                    }
                  </div>
                </a>
                <div className="-mr-2">
                  <Popover.Button className={classNames(
                    'bg-white dark:bg-black rounded-md p-2 inline-flex items-center justify-center text-gray-400',
                    'hover:text-gray-500 dark:text-gray-50 dark:hover:text-white hover:bg-gray-100 focus:outline-none',
                    'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
                  )}
                  >
                    <span className="sr-only">
                      <FormattedMessage
                        defaultMessage="Cerrar menú"
                        description="navbar button"
                      />
                    </span>
                    <XIcon className="h-6 w-6" aria-hidden="true" onClick={handleMobileClick} />
                  </Popover.Button>
                </div>
              </div>
            </div>
            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 100px)' }}>
              <div className="pt-5 pb-6 px-5">
                {/** @deprecated: use HeaderProductsMenu */}
                {!!data.businessUnit?.length && data.labelProductsByBu && (
                <HeaderBU data={data} />
                )}
                {!!data.productsMenu?.length && !!data.labelProductsMenu && (
                <HeaderProductsMenu data={data.productsMenu} clusters={data.clusters} />
                )}

                {data.productsLabel && (
                <div className="mt-6">
                  <nav className="grid gap-6">
                    {data.products?.map((item) => {
                      const vertical = getVerticalByLink(item.link, data.verticals, data.clusters);
                      const verticalColor = getColor(vertical.color);

                      return (
                        <LinkWithQuery
                          key={nanoid()}
                          href={Link.url(item.link, prismicLinkResolver)}
                          className="-m-3 p-3 flex items-stretch rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <div
                            className={classNames('rounded-sm', verticalColor.bg)}
                            style={{ minWidth: '4px', backgroundColor: item.color }}
                          />
                          <div className="ml-4 text-base font-medium text-gray-900 dark:text-white">
                            {item.title}
                          </div>
                        </LinkWithQuery>
                      );
                    })}
                  </nav>
                </div>
                )}

                {data.seeMoreProductsLink && data.seeMoreProductsLabel && (
                <div className="mt-6">
                  <LinkWithQuery
                    href={Link.url(
                      data.seeMoreProductsLink,
                      prismicLinkResolver,
                    )}
                    className={classNames(
                      'font-medium text-primary-600 hover:text-primary-500 dark:text-white dark:hover:text-primary-400',
                      'transition ease-in-out duration-150',
                    )}
                  >
                    {data.seeMoreProductsLabel}
                    {' '}
                    <span aria-hidden="true">&rarr;</span>
                  </LinkWithQuery>
                </div>
                )}
              </div>
              <div className="py-6 px-5">
                <div className="grid grid-cols-2 gap-4">
                  {data.links?.map((link) => (
                    <LinkWithQuery
                      key={nanoid()}
                      href={Link.url(link.link, prismicLinkResolver)}
                      className={classNames(
                        'text-base font-medium text-gray-900 hover:text-gray-700',
                        'dark:text-gray-300 dark:hover:text-white',
                      )}
                    >
                      {link.label}
                    </LinkWithQuery>
                  ))}
                </div>
                {data.loginButton && (
                <div className="mt-6">
                  <button
                    type="button"
                    className={classNames(
                      'w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md',
                      'shadow-sm text-base font-medium text-white dark:text-black bg-primary-600',
                      'hover:bg-primary-700',
                    )}
                  >
                    <FormattedMessage
                      defaultMessage="Ingresar"
                      description="login button"
                    />
                  </button>
                </div>
                )}
              </div>
              {!!data.content?.length && (
              <div className="py-6 px-5">
                <div className="grid grid-cols-2 gap-4">
                  {data.content?.map((item) => (
                    <LinkWithQuery
                      key={nanoid()}
                      href={Link.url(item.link, prismicLinkResolver)}
                      target={item.link?.target ?? '_self'}
                      className={classNames(
                        'text-base font-medium text-gray-500 hover:text-gray-900',
                        'dark:text-gray-300 dark:hover:text-white',
                      )}
                    >
                      {item.label}
                    </LinkWithQuery>
                  ))}
                </div>
              </div>
              )}
            </div>
          </div>
        </Popover.Panel>
      </Transition>
      {(!!slices?.length && resolver) && <SliceZone resolver={resolver} slices={slices} />}
    </Popover>
  );
};

export default Header;
